import React, {createRef, useEffect, useMemo, useState} from 'react';
import {Question} from '@agenium-ai/agenium-client-sdk/dist/models';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {stripHtml} from 'string-strip-html';
import {makeStyles} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import csx from 'classnames';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

export interface AgTextInputProps {
    question: Question;
    onChange?: (s: string | number) => void;
    value: string | number | null | undefined;
    type?: 'text' | 'numeric';
    forceStyle?: 'button' | 'dropdown' | 'radio';
    isInternalQuestion?: boolean;
}

const useStyles = makeStyles(() => ({
    button_active: {
        backgroundColor: '#E8EBF8',
    },
    title: {
        marginBottom: 40,
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '32px',
        lineHeight: '120%',
        color: '#456d69',
        '& p': {
            margin: 0,
            padding: 0,
        },
        '& p + p': {
            fontFamily: 'FredokaSemiBold',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '150%',
            color: '#456d69',
        },
    },
    button: {
        paddingTop: 16,
        paddingBottom: 16,
        fontSize: 26,
        lineHeight: '150%',
        textTransform: 'none',
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        height: 72,
        maxWidth: 288,
        color: '#174943',
        border: '1.75px solid',
        borderRadius: '8px',
        borderColor: '#456D69',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#F4F6FF',
        },
    },
    select: {
        '& legend': {
            overflow: 'hidden',
        },
        '& fieldset legend': {
            maxWidth: 0,
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#174943',
            borderWidth: 1,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#174943',
            borderRadius: 1,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: 'transparent',
        },
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            boxSizing: 'border-box',
            padding: '18px 24px',
        },

        height: 80,
        boxSizing: 'border-box',
        fontFamily: 'FredokaRegular',
        fontSize: 20,
        lineHeight: '120%',
        color: '#174943',

        '& ::placeholder': {
            color: '#74928E',
        },
    },
    selectLabel: {
        background: '#fff',
        paddingLeft: '10px',
        paddingRight: '15px',
        '&.MuiFormLabel-root': {
            fontSize: '14px',
            fontFamily: 'FredokaRegular',
            color: '#174943',
            '& .Mui-focused': {
                color: '#174943',
            },
        },
    },
    menuItem: {
        '&.Mui-selected': {backgroundColor: 'transparent'},
    },
    radio: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '1.7px solid #174943',
        boxSizing: 'border-box',
    },
    radioChecked: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '2px solid #174943',
        boxSizing: 'border-box',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#5AE288',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    },
    radioRoot: {
        paddingRight: 24,
        '&.MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            width: 17,
            height: 17,
            borderRadius: '50%',
            backgroundColor: 'transparent',
        },
    },
    label: {
        color: '#000',
        fontSize: 18,
        fontFamily: 'FredokaLight',
        lineHeight: '120%',
        fontWeight: 300,
    },
    radioContainer: {
        '&:hover $radioRoot::after': {
            backgroundColor: '#D9D9D9',
        },
        '&:hover .Mui-checked$radioRoot::after': {
            backgroundColor: 'transparent',
        },
    },
    internal: {
        '& $button': {
            color: '#794D65',
            borderColor: '#C69AB1',
        },
        '& $button_active': {
            backgroundColor: '#F7F1F4',
        },
    },
}));

const MultipleChoiceSingleInput = (props: AgTextInputProps) => {
    const classes = useStyles();
    const [menuWidth, setMenuWidth] = useState(300);
    const selectRef = createRef();
    useEffect(() => {
        if (selectRef.current) {
            const {current} = selectRef as React.RefObject<any>;
            const boundingRect = current.getBoundingClientRect();
            const {width} = boundingRect;
            if (whatShouldBeRendered === 'dropdown') setMenuWidth(Math.round(width));
        }
    }, [selectRef]);
    const {question, value, onChange, forceStyle, isInternalQuestion} = props;
    const [defaultValue] = useState(value ? value : '');
    const [whatShouldBeRendered, setWhatShouldBeRendered] = useState<'button' | 'dropdown' | 'radio' | undefined>();
    function handleChange(e: any) {
        const v: string = e.target.value;
        onChange?.(v);
    }
    useEffect(() => {
        if (question.allowedValues) {
            if (forceStyle === 'dropdown' || (!forceStyle && question.allowedValues.length > 5))
                setWhatShouldBeRendered('dropdown');
            else if (
                forceStyle === 'button' ||
                (!forceStyle &&
                    question.allowedValues.length < 4 &&
                    !question.allowedValues.find((o) => o.displayName.length > 12))
            )
                setWhatShouldBeRendered('button');
            else setWhatShouldBeRendered('radio');
        }
    }, [forceStyle, question.allowedValues]);

    const sanitizedText = useMemo(
        () => stripHtml(question.questionText?.replaceAll('&nbsp;', ' ') || '').result,
        [question.questionText],
    );

    const shouldUseH1 = sanitizedText.length * 7 > menuWidth;
    const containsSpecialChar = sanitizedText.includes(':') || sanitizedText.includes('?');

    switch (whatShouldBeRendered) {
        case 'button':
            return (
                <Box className={csx({[classes.internal]: isInternalQuestion})}>
                    <Typography component="h1" variant="h4" classes={{root: classes.title}}>
                        {parse(
                            DOMPurify.sanitize(question.questionText?.replaceAll('&nbsp;', ' '), {
                                USE_PROFILES: {html: true},
                            }),
                        )}
                    </Typography>
                    <Grid container style={{marginBottom: 2, gap: 24}}>
                        {question.allowedValues.map((v) => (
                            <Grid item xs key={v.id} style={{maxWidth: 288}}>
                                <Button
                                    name={v.id}
                                    fullWidth
                                    variant="outlined"
                                    classes={{
                                        root: `${classes.button} ${value === v.id ? classes.button_active : ''}`,
                                    }}
                                    onClick={() => onChange?.(v.id)}
                                >
                                    {v.displayName}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            );
        case 'dropdown':
            return (
                <>
                    {shouldUseH1 ? (
                        <Typography component="h1" variant="h4" classes={{root: classes.title}}>
                            {parse(
                                DOMPurify.sanitize(question.questionText?.replaceAll('&nbsp;', ' '), {
                                    USE_PROFILES: {html: true},
                                }),
                            )}
                        </Typography>
                    ) : (
                        <Typography component="h2" className={classes.title}>
                            {containsSpecialChar ? sanitizedText : `Please provide ${sanitizedText}`}
                        </Typography>
                    )}
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="multipleChoiceSingleInput" shrink={true} className={classes.selectLabel}>
                            {sanitizedText}
                        </InputLabel>
                        <Select
                            ref={selectRef}
                            displayEmpty
                            className={classes.select}
                            labelId="multipleChoiceSingleInput"
                            label={sanitizedText}
                            fullWidth
                            autoWidth
                            name={question.key}
                            onChange={handleChange}
                            defaultValue={defaultValue}
                            placeholder="Select an Answer"
                            value={props.value}
                            IconComponent={ExpandMoreIcon}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 224,
                                        width: menuWidth,
                                    },
                                },
                            }}
                        >
                            <MenuItem disabled value="" className={classes.menuItem}>
                                Select an Answer
                            </MenuItem>
                            {question.allowedValues.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {v.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            );
        case 'radio':
            return (
                <FormControl component="fieldset">
                    <FormLabel component="legend">
                        <Typography component="h1" variant="h4" classes={{root: classes.title}}>
                            {parse(
                                DOMPurify.sanitize(question.questionText?.replaceAll('&nbsp;', ' '), {
                                    USE_PROFILES: {html: true},
                                }),
                            )}
                        </Typography>
                    </FormLabel>
                    <RadioGroup
                        aria-label={sanitizedText}
                        name={question.key}
                        value={value ?? ''}
                        onChange={handleChange}
                    >
                        {question.allowedValues.map((v) => (
                            <FormControlLabel
                                key={v.id}
                                value={v.id}
                                classes={{root: classes.radioContainer, label: classes.label}}
                                control={
                                    <Radio
                                        disableRipple
                                        classes={{root: classes.radioRoot}}
                                        checkedIcon={<Box className={classes.radioChecked} />}
                                        icon={<Box className={classes.radio} />}
                                    />
                                }
                                label={v.displayName}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        default:
            return <></>;
    }
};

export default MultipleChoiceSingleInput;
